import {
  CurrencyCode,
  enumLoadStatus,
  enumUnit,
  ReceivedLineItemInput,
  Unit,
  WeightInput,
} from '@rematter/frontend-lib-api-client';
import Big from 'big.js';

import { SalesOrderUpdateParams } from '../_graphql/mutationSalesOrderUpdate';
import {
  enumLoadWeightType,
  LoadFormLineItem,
  LoadFormState,
  LoadRecord,
} from '../types/load';

import { transformFormLineItemsToInput } from './loadLineItems';
import { toMoneyInput, zeroMoneyInput } from './money';
import { toWeightInput, zeroWeightInput } from './weight';

export type LoadFormTab = 'load-info' | 'materials' | 'notes' | 'shipped';

export const extractDatesAndEntitiesFromLoad = (
  load: LoadRecord | LoadFormState,
): Partial<LoadFormState> => ({
  ...(load.expectedShipDate && { expectedShipDate: load.expectedShipDate }),
  ...(load.facilityId && { facilityId: load.facilityId }),
  ...(load.salesOrderId && { salesOrderId: load.salesOrderId }),
  ...(load.bookingId && { bookingId: load.bookingId }),
  ...(load.shippedDate && { shippedDate: load.shippedDate }),
  ...(load.loadReceivedDate && { loadReceivedDate: load.loadReceivedDate }),
  // Include weight fields to preserve them during updates
  ...(load.heavyWeight !== undefined && { heavyWeight: load.heavyWeight }),
  ...(load.lightWeight !== undefined && { lightWeight: load.lightWeight }),
});

type TransformLoadFormToInputParams = {
  form: LoadFormState;
  tab?: LoadFormTab;
  onlyDatesAndEntities?: boolean;
  tagToLineItemMap?: Map<string, string>;
};

export const transformLoadFormToInput = ({
  form,
  tab,
  onlyDatesAndEntities,
  tagToLineItemMap,
}: TransformLoadFormToInputParams): Record<string, unknown> => {
  const input: Record<string, unknown> = {};

  if (onlyDatesAndEntities) {
    return extractDatesAndEntitiesFromLoad(form);
  }

  const lineItems = transformFormLineItemsToInput(
    form.lineItems ?? [],
    tagToLineItemMap,
  );

  if (tab === 'notes') {
    if (form.internalNotes) {
      input.internalNotes = form.internalNotes;
    }
    if (form.externalNotes) {
      input.externalNotes = form.externalNotes;
    }
    return input;
  }

  if (tab === 'load-info') {
    if (form.expectedShipDate) {
      input.expectedShipDate = form.expectedShipDate;
    }

    if (form.facilityId) {
      input.facilityId = form.facilityId;
    }

    if (form.currencyCode) {
      input.currencyCode = form.currencyCode;
    }

    if (form.salesOrderId) {
      input.salesOrderId = form.salesOrderId;
    }

    if (form.bookingId) {
      input.bookingId = form.bookingId;
    }

    if (form.shippingCarrierName) {
      input.shippingCarrierName = form.shippingCarrierName;
    }
    if (form.scac) {
      input.scac = form.scac;
    }
    if (form.freightForwarder) {
      input.freightForwarder = form.freightForwarder;
    }
    if (form.truckFreight) {
      input.truckFreight = form.truckFreight;
    }
    if (form.deliveryNumber) {
      input.deliveryNumber = form.deliveryNumber;
    }
    if (form.releaseNumber) {
      input.releaseNumber = form.releaseNumber;
    }
    if (form.driverName) {
      input.driverName = form.driverName;
    }
    if (form.truckNumber) {
      input.truckNumber = form.truckNumber;
    }
    if (form.trailerNumber) {
      input.trailerNumber = form.trailerNumber;
    }
    if (form.containerTypeId) {
      input.containerTypeId = form.containerTypeId;
    }
    if (form.containerNumber) {
      input.containerNumber = form.containerNumber;
    }
    if (form.sealNumber) {
      input.sealNumber = form.sealNumber;
    }

    if (form.lineItems.length > 0) {
      input.lineItems = lineItems;
    }

    return input;
  }

  if (tab === 'materials') {
    // Include weight fields if they exist - clean them to remove __typename
    if (form.heavyWeight !== undefined) {
      input.heavyWeight = toWeightInput(form.heavyWeight);
    }
    if (form.lightWeight !== undefined) {
      input.lightWeight = toWeightInput(form.lightWeight);
    }

    // Only load info related fields (including line items)
    if (form.lineItems.length > 0) {
      input.lineItems = lineItems;
    }

    if (form.weightDisplayUnit) {
      input.weightDisplayUnit = form.weightDisplayUnit;
    }

    return input;
  }

  if (tab === 'shipped') {
    // Only load info related fields (including line items)
    if (form.lineItems.length > 0) {
      input.lineItems = lineItems;
    }

    return input;
  }

  throw new Error(`Invalid tab: ${tab}`);
};

const hasNonZeroValue = (weight: { value: unknown } | undefined | null) =>
  weight != null && Number(weight.value) !== 0;

const resolveGrossWeight = (
  weights: LoadFormLineItem['weights'],
): WeightInput => {
  if (hasNonZeroValue(weights?.[enumLoadWeightType.Gross])) {
    return (
      toWeightInput(weights?.[enumLoadWeightType.Gross]) ?? zeroWeightInput()
    );
  }

  if (
    !hasNonZeroValue(weights?.[enumLoadWeightType.Tare]) &&
    hasNonZeroValue(weights?.[enumLoadWeightType.Net])
  ) {
    return (
      toWeightInput(weights?.[enumLoadWeightType.Net]) ?? zeroWeightInput()
    );
  }

  return zeroWeightInput();
};

const resolveWeight = (
  weights: LoadFormLineItem['weights'],
  type: enumLoadWeightType,
): WeightInput => toWeightInput(weights?.[type]) ?? zeroWeightInput();

export const transformLoadLineItemsToReceivedLineItems = (
  lineItems: LoadFormLineItem[],
): ReceivedLineItemInput[] =>
  lineItems.map((item) => ({
    loadLineItemId: item.id,
    materialType: 'Raw',
    grossWeight: resolveGrossWeight(item.weights),
    tareWeight: resolveWeight(item.weights, enumLoadWeightType.Tare),
    unitPrice: item.unitPrice ? toMoneyInput(item.unitPrice) : zeroMoneyInput(),
    pricingUnit: item.pricingUnit ?? enumUnit.Pounds,
    itemId: item.itemId as string,
    notes: '',
  }));

type SalesOrderLineItem = {
  id: string;
  item: {
    id: string;
  };
  quantity: number;
  totalPrice: {
    amount: number;
    currencyCode: CurrencyCode;
  };
  unit: Unit;
  unitPrice: {
    amount: number;
    currencyCode: CurrencyCode;
  };
};

export const transformLoadFormToSalesOrderUpdateParams = (params: {
  form: LoadFormState;
  salesOrderLineItems: SalesOrderLineItem[];
  queriedSalesOrderId?: string;
}): SalesOrderUpdateParams | undefined => {
  const { form, salesOrderLineItems, queriedSalesOrderId } = params;

  if (
    queriedSalesOrderId &&
    form.salesOrderId &&
    queriedSalesOrderId !== form.salesOrderId
  ) {
    return;
  }

  const input = {} as SalesOrderUpdateParams;

  const existingSalesOrderItemIds = new Set(
    salesOrderLineItems.map((item) => item.item.id),
  );

  const lineItemsToAddToSalesOrder = form.lineItems.filter(
    (item) =>
      item.includeInSalesOrder === true &&
      item.itemId &&
      !existingSalesOrderItemIds.has(item.itemId),
  );

  if (!lineItemsToAddToSalesOrder.length) {
    return;
  }

  if (form.salesOrderId) {
    input.id = form.salesOrderId;
  }

  const mappedLineItemsToAddToSalesOrder = lineItemsToAddToSalesOrder.map(
    (item) => {
      const quantity = new Big(
        item.weights?.[enumLoadWeightType.Net]?.value ?? 0,
      ).toNumber();
      return {
        itemId: item.itemId as string,
        quantity,
        unitPrice: item.unitPrice
          ? toMoneyInput(item.unitPrice)
          : zeroMoneyInput(),
        totalPrice: item.unitPrice
          ? toMoneyInput({
              amount: new Big(item.unitPrice.amount).times(quantity).toNumber(),
              currencyCode: item.unitPrice.currencyCode,
            })
          : zeroMoneyInput(),
        unit: item.weights?.[enumLoadWeightType.Net]?.unit ?? enumUnit.Pounds,
      };
    },
  );

  const mappedSalesOrderLineItems = salesOrderLineItems.map((item) => ({
    id: item.id,
    itemId: item.item.id,
    quantity: item.quantity,
    unit: item.unit,
    unitPrice: item.unitPrice,
    totalPrice: item.totalPrice,
  }));

  if (mappedLineItemsToAddToSalesOrder.length > 0) {
    input.lineItems = [
      ...mappedSalesOrderLineItems,
      ...mappedLineItemsToAddToSalesOrder,
    ];
  }

  return input;
};

export const loadHasBeenShipped = (status?: LoadRecord['status']): boolean =>
  status === enumLoadStatus.Shipped ||
  status === enumLoadStatus.Received ||
  status === enumLoadStatus.Reconciled;

export const loadHasBeenReceived = (status?: LoadRecord['status']): boolean =>
  status === enumLoadStatus.Received || status === enumLoadStatus.Reconciled;
